/**
 * Pet Parent Hub — 30-Day Checklist content (dog + cat).
 *
 * Ported from the design handoff (Claude Design "Pet Parent Hub - 30-Day Checklist").
 * Task `id`s are the stable `task_key`s persisted in adopter_checklist_completion —
 * do not rename them once shipped. `{pet}` tokens are replaced at render time with
 * "your dog"/"your cat" (see fillPet in checklistUtils) — never the animal's stored
 * name, which is frequently wrong or a placeholder in shelter data.
 *
 * Checklist items are manually checked by the adopter; guidance bullets are
 * informational only (not individually checkable).
 */

export type AgeBracket = 'under6' | 'over6';
export type Region = 'US' | 'CA' | 'INTL';

export interface ChecklistCard {
  kind: 'lesson' | 'partner';
  title: string;
  meta: string;
  url: string;
  cta: string;
  image?: string;
  tint?: string;
  icon?: string;
  external?: boolean;
  /** 'contain' renders the image letterboxed on white (logos). Default is cover. */
  fit?: 'contain';
  /** When set, the card only renders for adopters in one of these regions. */
  regions?: Region[];
  conditional?: boolean;
  under6?: { title: string; url: string };
  over6?: { title: string; url: string };
}

export interface ChecklistTaskData {
  id: string;
  title: string;
  summary: string;
  guidance: string[];
  cards: ChecklistCard[];
  /** When set, the task only appears for pets in this age bracket (foster checklists). */
  ageOnly?: AgeBracket;
}

export interface ChecklistStageData {
  id: string;
  range: string;
  name: string;
  short: string;
  desc: string;
  tasks: ChecklistTaskData[];
}

export interface ChecklistData {
  species: 'dog' | 'cat';
  stages: ChecklistStageData[];
}

// Soft brand pastels used behind icon-placeholder lesson thumbnails
export const TINT = {
  purple: '#F2E7FE',
  teal: '#C8FFF4',
  amber: '#FFF3E0',
  pink: '#FCE7F3',
  green: '#F1F8E9',
  blue: '#DDE7F3',
};

const PETPLACE_LOGO = '/hub/checklist/petplace-logo.jpg';

export const lesson = (
  title: string,
  meta: string,
  url: string,
  opts: Partial<ChecklistCard> = {},
): ChecklistCard => ({
  kind: 'lesson',
  title,
  meta: meta || '4-minute lesson',
  url,
  cta: opts.cta || 'Open',
  image: opts.image,
  tint: opts.tint || TINT.purple,
  icon: opts.icon || 'school',
  external: opts.external || false,
  ...(opts.fit ? { fit: opts.fit } : {}),
  ...(opts.conditional ? { conditional: true, under6: opts.under6, over6: opts.over6 } : {}),
});

export const guide = (
  title: string,
  meta: string,
  url: string,
  opts: Partial<ChecklistCard> = {},
): ChecklistCard => lesson(title, meta, url, { icon: 'menu_book', ...opts });

// 24Petwatch operates in the US and Canada; pet insurance referral is US-only.
const microchipCard: ChecklistCard = {
  ...lesson(
    '24Petwatch by PetPlace Microchip Registry',
    'Register or update your contact details on the national registry.',
    'https://www.petplace.com/pet-microchip-registry?utm_medium=referral&utm_source=petcademy',
    { cta: 'Go to Site', image: PETPLACE_LOGO, external: true },
  ),
  fit: 'contain',
  regions: ['US', 'CA'],
};

const insuranceCard: ChecklistCard = {
  ...lesson(
    'Find the Best Insurance Plan',
    'Compare coverage options tailored to {pet}.',
    'https://www.petplace.com/insurance/?utm_medium=referral&utm_source=petcademy',
    { cta: 'Go to Site', image: PETPLACE_LOGO, external: true },
  ),
  fit: 'contain',
  regions: ['US'],
};

const albumCard: ChecklistCard = lesson(
  'Welcome Home Album',
  'Add photos and share milestones from your first month.',
  '/hub/albums',
  { image: '/hub/checklist/album-share-card.png', cta: 'Open', icon: 'photo_album' },
);

const vetVisitGuidance = [
  'Schedule a wellness exam within the first two weeks after adoption',
  'Bring any medical records you received from the shelter or rescue',
  "Ask your veterinarian to scan and verify {pet}'s microchip information",
];

const microchipGuidance = [
  "Confirm which company manages {pet}'s microchip registration",
  "Contact your shelter or rescue if you're unsure where {pet} is registered",
  "Log in and verify {pet}'s name and your contact information are up to date",
];

const insuranceGuidance = [
  "Unexpected veterinary expenses can happen at any stage of {pet}'s life",
  'Pet insurance may help reduce the financial impact of accidents and illnesses',
  'Compare multiple providers to find coverage that fits {pet} and budget',
];

const albumGuidance = [
  "Capture special moments from {pet}'s first month at home",
  'Celebrate milestones big and small as your bond grows',
  'Share memories with family and friends who are cheering you on',
];

const welcomeGuidance = [
  'Keep your home calm and quiet during the first few days to help {pet} decompress',
  'Wait at least 72 hours before introducing visitors or busy social activities',
  'Introduce other pets gradually and move at the pace of the most cautious animal',
];

export const DOG_CHECKLIST: ChecklistData = {
  species: 'dog',
  stages: [
    {
      id: 'd-s1',
      range: 'Days 1–3',
      name: 'Decompression & Safety',
      short: 'Decompression',
      desc: 'Keep the house quiet during this phase. If you have other animals, keep them separated while {pet} adjusts.',
      tasks: [
        {
          id: 'd4',
          title: "Review the 'Welcoming Your Adopted Dog Home' guide",
          summary: 'The essentials for a calm, confident first week together.',
          guidance: welcomeGuidance,
          cards: [guide(
            'Welcoming Your Adopted Dog Home',
            'Guide · 8-minute read',
            '/hub/lessons-and-guides/welcoming-your-adopted-dog-home',
            { image: '/hub/checklist/welcoming-home.png', tint: TINT.purple, icon: 'home' },
          )],
        },
        {
          id: 'd1',
          title: 'Set Up a Safe Space',
          summary: 'Create a quiet, comfortable area where your dog can decompress and feel secure.',
          guidance: [
            'Choose a quiet room or area away from foot traffic',
            'Add a crate, comfy bed, and water bowl',
            "Let {pet} explore at their own pace — don't force interaction",
          ],
          cards: [lesson(
            'Creating a Safe Space for Your Dog',
            '4-minute lesson',
            '/hub/lessons-and-guides/creating-a-safe-space-for-your-dog',
            { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FNfFKnIRwRaWxQm5UO38M_Crate%20training%201.png', tint: TINT.teal },
          )],
        },
        {
          // Consolidates the former feeding (d2) + walk & potty (d3) tasks; keeps
          // the d2 task_key so previously-saved completion carries over.
          id: 'd2',
          title: 'Establish a Potty & Daily Routine',
          summary: 'A consistent rhythm for meals, potty breaks, and play builds security and trust.',
          guidance: [
            'Follow a consistent schedule for meals, potty breaks, rest, and play',
            'Take your dog outside frequently and reward potty successes',
            'Balance training, enrichment, and downtime to support a smooth adjustment',
          ],
          cards: [
            lesson(
              'Your Dog’s Daily Routine',
              '5-minute lesson',
              '/hub/lessons-and-guides/your-dogs-daily-routine',
              {
                conditional: true,
                image: '/hub/checklist/harry-getting-settled.png',
                icon: 'restaurant',
                tint: TINT.amber,
                under6: { title: "Your Puppy's Daily Routine", url: '/hub/lessons-and-guides/your-puppys-daily-routine' },
                over6: { title: 'Your Dog’s Daily Routine', url: '/hub/lessons-and-guides/your-dogs-daily-routine' },
              },
            ),
            lesson(
              'House Training',
              '4-minute lesson',
              '/hub/lessons-and-guides/house-training',
              { image: '/hub/checklist/house-training.png', tint: TINT.green },
            ),
          ],
        },
      ],
    },
    {
      id: 'd-s2',
      range: 'Days 4–14',
      name: 'Foundations',
      short: 'Foundations',
      desc: 'Establish good habits and take care of the essentials that keep your dog safe and healthy.',
      tasks: [
        {
          id: 'd5',
          title: 'Confirm Microchip Information',
          summary: "Make sure your dog's microchip information is up to date.",
          guidance: microchipGuidance,
          cards: [microchipCard],
        },
        {
          id: 'd6',
          title: 'Start Basic Training',
          summary: 'Short, positive sessions build confidence and a shared language.',
          guidance: [
            'Positive reinforcement training helps {pet} learn through rewards rather than punishment',
            'Choose rewards {pet} loves, such as treats, toys, or praise',
            'Decide whether you\'ll use a clicker or a marker word like "Yes!" and use it consistently',
          ],
          cards: [
            lesson(
              'Introduction to Positive Reinforcement Training',
              '4-minute lesson',
              '/hub/lessons-and-guides/introduction-to-positive-reinforcement-training',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FZ5J6muoSQUKRbE5arwjW_Screen%20Shot%202023-11-06%20at%203.13.30%20PM.png', tint: TINT.green, icon: 'menu_book' },
            ),
            lesson(
              'Building Good Associations',
              '5-minute lesson',
              '/hub/lessons-and-guides/building-good-associations',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FyEwrR0ChRnayKnNbsVIo_Building%20good%20associations.png', tint: TINT.teal, icon: 'favorite' },
            ),
            lesson(
              'Name Recognition',
              '3-minute lesson',
              '/hub/lessons-and-guides/name-recognition',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2Fsi3qHvO4Thehi50kSmm7_Name.png', tint: TINT.amber, icon: 'badge' },
            ),
          ],
        },
        {
          id: 'd7',
          title: 'Schedule Vet Visit',
          summary: 'A first wellness check sets a healthy baseline for your dog.',
          guidance: vetVisitGuidance,
          cards: [],
        },
        {
          id: 'd8',
          title: 'Explore Pet Insurance',
          summary: 'Peace of mind for the unexpected — compare plans before you need them.',
          guidance: insuranceGuidance,
          cards: [insuranceCard],
        },
      ],
    },
    {
      id: 'd-s3',
      range: 'Days 15–30',
      name: 'Confidence & Connection',
      short: 'Stability',
      desc: 'Deepen your bond and celebrate how far you and your dog have come.',
      tasks: [
        {
          id: 'd9',
          title: 'Continue Basic Training',
          summary: "Keep building your dog's skills with new cues and behaviors.",
          guidance: [
            'Practice Touch, Sit, and Down in different rooms and locations',
            'Use high-value rewards when introducing new distractions',
            'Celebrate small improvements and build confidence through repetition',
          ],
          cards: [
            lesson(
              'Sit',
              '3-minute lesson',
              '/hub/lessons-and-guides/sit',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FDVipo7B9TZ2PXRzBQHtt_sit.png', icon: 'pets', tint: TINT.purple },
            ),
            lesson(
              'Touch',
              '3-minute lesson',
              '/hub/lessons-and-guides/touch',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FJwWVBpRiSEygkSDGJAog_Touch_Lesson.png', icon: 'touch_app', tint: TINT.amber },
            ),
            lesson(
              'Down',
              '5-minute lesson',
              '/hub/lessons-and-guides/down',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FdDFkQY9mRiaOS8mlW6Ci_Down.png', icon: 'pets', tint: TINT.pink },
            ),
          ],
        },
        {
          id: 'd10',
          title: 'Complete Your Welcome Home Album',
          summary: "Capture your dog's first month of milestones to look back on.",
          guidance: albumGuidance,
          cards: [albumCard],
        },
      ],
    },
  ],
};

export const CAT_CHECKLIST: ChecklistData = {
  species: 'cat',
  stages: [
    {
      id: 'c-s1',
      range: 'Days 1–3',
      name: 'Decompression & Safety',
      short: 'Decompression',
      desc: 'Help {pet} settle into their new environment and feel safe enough to explore.',
      tasks: [
        {
          id: 'c0',
          title: "Review the 'Welcoming Your Adopted Cat Home' Guide",
          summary: 'The essentials for a calm, confident first week together.',
          guidance: welcomeGuidance,
          cards: [guide(
            'Welcoming Your Adopted Cat Home',
            'Guide · 8-minute read',
            '/hub/lessons-and-guides/welcoming-your-adopted-cat-home',
            { image: 'https://umsousercontent.com/lib_QOzUiRhzXcXZiVKF/0lv5ccxt8nqh8pt8.png', tint: TINT.purple, icon: 'home' },
          )],
        },
        {
          id: 'c1',
          title: 'Set Up A Safe Space',
          summary: 'Create a quiet, comfortable area where your cat can decompress and feel secure.',
          guidance: [
            'Choose a small, quiet room to start',
            'Add a litter box, food, water, and a hiding spot',
            'Let {pet} come to you — patience builds trust',
          ],
          cards: [
            lesson(
              'Creating a Safe Space for Your Cat',
              '4-minute lesson',
              '/hub/lessons-and-guides/creating-a-safe-space-for-your-cat',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FRdcPJlNSSC1ONkLQiogg_Safe%20Space%20Cat.png', tint: TINT.teal },
            ),
            lesson(
              'Hiding',
              '3-minute lesson',
              '/hub/lessons-and-guides/hiding',
              { image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2FB0UUnTPNRg6ssx3ytz7s_Cat%20Hiding.png', icon: 'visibility_off', tint: TINT.purple },
            ),
          ],
        },
        {
          id: 'c2',
          title: 'Implement Daily Routine',
          summary: 'Regular meals in a calm spot help a new cat feel at home.',
          guidance: [
            'Feed, play, and interact at consistent times each day to help your cat feel secure',
            'Schedule short play sessions in the early morning and evening when cats are naturally most active',
            'Give your cat time to rest and explore at their own pace between activities',
          ],
          cards: [lesson(
            "Your Cat's Daily Routine",
            '5-minute lesson',
            '/hub/lessons-and-guides/your-cats-daily-routine',
            {
              conditional: true,
              icon: 'restaurant',
              tint: TINT.amber,
              image: 'https://import.cdn.thinkific.com/256766%2Fcustom_site_themes%2Fid%2F96zMXdHjTtqAW9yJ2dKl_Litter%20Box%20Issues.png',
              under6: { title: "Your Kitten's Daily Routine", url: '/hub/lessons-and-guides/your-kittens-daily-routine' },
              over6: { title: "Your Cat's Daily Routine", url: '/hub/lessons-and-guides/your-cats-daily-routine' },
            },
          )],
        },
      ],
    },
    {
      id: 'c-s2',
      range: 'Days 4–14',
      name: 'Foundations',
      short: 'Foundations',
      desc: 'Take care of the essentials that keep your cat safe and healthy.',
      tasks: [
        {
          id: 'c4',
          title: 'Confirm Microchip Information',
          summary: "Make sure your cat's microchip information is up to date.",
          guidance: microchipGuidance,
          cards: [microchipCard],
        },
        {
          id: 'c5',
          title: 'Schedule Vet Visit',
          summary: 'A first wellness check sets a healthy baseline for your cat.',
          guidance: vetVisitGuidance,
          cards: [],
        },
        {
          id: 'c6',
          title: 'Explore Pet Insurance',
          summary: 'Peace of mind for the unexpected — compare plans before you need them.',
          guidance: insuranceGuidance,
          cards: [insuranceCard],
        },
      ],
    },
    {
      id: 'c-s3',
      range: 'Days 15–30',
      name: 'Confidence & Connection',
      short: 'Stability',
      desc: "Deepen your bond and keep your cat's mind and body engaged.",
      tasks: [
        {
          id: 'c7',
          title: 'Incorporate Daily Enrichment',
          summary: 'Play and enrichment keep your cat happy and confident.',
          guidance: [
            'Play with your cat 2–3 times per day using interactive toys',
            'Provide scratching posts, cat trees, and other opportunities to climb, scratch, and explore',
            'Focus enrichment activities in the early morning and evening when cats are naturally most active',
          ],
          cards: [lesson(
            'Enrichment',
            '5-minute lesson',
            '/hub/lessons-and-guides/enrichment',
            { image: 'https://umsousercontent.com/lib_QOzUiRhzXcXZiVKF/iwla24rm9eqhftyr.png', icon: 'extension', tint: TINT.green },
          )],
        },
        {
          id: 'c8',
          title: 'Complete Your Welcome Home Album',
          summary: "Capture your cat's first month of milestones to look back on.",
          guidance: albumGuidance,
          cards: [albumCard],
        },
      ],
    },
  ],
};
